// Where the retired Trip tab's paths land now: the departures board.
//
// There was a Trip tab at `/tabs/directions`. The board with a destination set
// plans the change, honours what to avoid and opens the same journey page, so
// the tab was retired and the network map took its slot. The PATH is kept, as a
// redirect, because paths outlive tabs: a notification action, a widget, an old
// deep link or a habit can still name it, and a router that answered one with
// nothing at all would boot to a blank outlet.
//
// Its own module rather than a function inside router.js, so the rule can be
// tested without loading the tab shell and Ionic into a Node test.

import { trip } from './directions-state.js';
import { requestBoard } from './board-request.js';
import { stationById } from './network.js';
import { servedAdapterModes } from './modes.js';
import { parseEndParam } from './mode-plan.js';
import {
  askDestinationSheet, keepDestination, stationEnd, stopEnd,
} from './trip-ends.js';

/** The board. Kept literal here: router.js derives the same path from the routes. */
export const TRIP_LANDING = '/tabs/departures';

/** The old Trip paths. `/tabs/trip` never shipped, and is here because it is the obvious guess. */
export const TRIP_PATHS = Object.freeze(['/tabs/directions', '/tabs/trip']);

/**
 * The redirect itself, as Vue Router calls it.
 *
 * With a FROM already in the stash — a journey opened a moment ago, a shared
 * trip that filled it — the board is asked to open at that station with the TO
 * as its destination, which is the screen that answers the same question
 * today. With nothing in the stash it is simply the board. A redirect rather
 * than a page, so nothing renders a screen that no longer exists and the tab
 * bar lights TRAINS, where the answer is.
 */
export function tripRedirect() {
  const { from, to } = trip.value ?? {};
  const station = from?.stopId ? stationById(from.stopId) ?? from : null;
  if (station) requestBoard(station, { destStopId: to?.stopId ?? null });
  return TRIP_LANDING;
}

// ── The retired Plan and Trip screens of a mode with stops of its own ────────
//
// Trams used to plan on a Plan screen of their own (`/tram-plan?from=…&to=…`)
// and read the result on a Trip screen (`/tram-trip/:index`). Trains plan on the
// board and read the result on the journey page, and one way of planning is the
// whole point: both screens are gone and both paths are kept, as redirects, for
// the same reason the Trip tab's was. Generated from the registry, so a mode
// added there gets its two redirects with no code of its own.

/** `/tabs/tram-plan` and `/tabs/tram-trip/:index`, for each mode that is served. */
export function modePaths(mode) {
  return { plan: `/tabs/${mode.id}-plan`, trip: `/tabs/${mode.id}-trip/:index` };
}

/**
 * Where an old plan link lands: the board of the place it started from, with the
 * place it was going to already chosen — or, with no destination in the link, the
 * destination sheet open. A start that is a station is the Trains board; a start
 * that is a stop of the mode is that stop's board, which has the same chip.
 */
export function planRedirect(mode, to) {
  const from = parseEndParam(to?.query?.from);
  const dest = parseEndParam(to?.query?.to);
  const destEnd = dest
    ? (dest.kind === 'station' ? stationEnd({ stopId: dest.stopId }) : stopEnd(
      servedAdapterModes().find((m) => m.id === dest.kind) ?? mode, dest.stopIds, '',
    ))
    : null;
  // The stop table is read for the name the chip prints, and may not have been
  // yet. The chip fills its name in when it lands.
  if (destEnd && destEnd.kind !== 'station') import('./mode-stops.js').then((m) => m.loadModeStops(mode)).catch(() => {});

  if (from && from.kind !== 'station') {
    const origin = stopEnd(mode, from.stopIds, '');
    if (destEnd) keepDestination(origin, destEnd); else askDestinationSheet();
    return `/tabs/${mode.id}-stop/${origin.stopId}`;
  }
  const station = from ? stationById(from.stopId) : null;
  if (station) {
    if (destEnd?.kind === 'station') requestBoard(station, { destStopId: destEnd.stopId });
    else {
      requestBoard(station);
      if (destEnd) keepDestination(stationEnd(station), destEnd); else askDestinationSheet();
    }
  }
  return TRIP_LANDING;
}

/**
 * Where an old trip link lands: the journey page, if the journey it names is
 * still the one in hand, and the board otherwise. There is nothing to rebuild a
 * plan from but the plan itself, and a plan is a statement about the next hour.
 */
export function tripPageRedirect(to) {
  const index = Number(to?.params?.index);
  const options = trip.value?.options ?? [];
  return Number.isInteger(index) && index >= 0 && options[index] ? `/tabs/journey/${index}` : TRIP_LANDING;
}

/** The route records the router adds, one pair per mode that has screens of its own. */
export function modeRedirects() {
  return servedAdapterModes().filter((m) => m.api).flatMap((mode) => {
    const p = modePaths(mode);
    return [
      { path: p.plan.replace(/^\/tabs\//, ''), redirect: (to) => planRedirect(mode, to) },
      { path: p.trip.replace(/^\/tabs\//, ''), redirect: tripPageRedirect },
    ];
  });
}
